import { Component, OnInit } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { combineLatest, merge, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { Router, RouterModule } from '@angular/router';

import { Globals } from '../shared/models/globals';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { ScheduleService } from '../shared/services/schedule.service';
import { ScoutingFlowService } from '../shared/services/scouting-flow.service';
import { FlowItemModel } from '../shared/models/FlowItemModel';
import { ScheduleEvent } from '../shared/models/ScheduleModels';
import { ScoutingActivityStatsViewModel } from '../shared/models/ScoutingActivityStatsViewModel';
import { CountItemModel } from '../shared/models/CountItemModel';
import { ReportDetailsDialogComponent } from '../scouting/components/report-details-dialog/report-details-dialog.component';
import { EventDetailsDialogComponent, EventDetailsDialogResult } from '../shared/components/event-details-dialog/event-details-dialog.component';
import { TranslateService } from '@ngx-translate/core';

interface ScoutInfoStats {
  reports: number | null;
  notes: number | null;
  gradedGames: number | null;
  scheduleActivities: number | null;
}

interface LoadingState<T> {
  loading: boolean;
  data: T;
}

interface OrganizationBrandVm {
  orgName: string;
  orgLogoPath: string;
}

interface ScoutInfoVm {
  seasonScoutInfo: ScoutInfoStats;
  lifetimeScoutInfo: ScoutInfoStats;
}

interface HomeVm {
  userName: string;
  orgName: string;
  orgLogoPath: string;
  dateLocale: string;
  scoutingStats: ScoutingActivityStatsViewModel | null;
  isLoadingStats: boolean;
  seasonScoutInfo: ScoutInfoStats;
  lifetimeScoutInfo: ScoutInfoStats;
  isLoadingScoutInfo: boolean;
  recentReports: FlowItemModel[];
  isLoadingReports: boolean;
  comingActivitiesCount: number;
  comingActivities: ScheduleEvent[];
  isLoadingActivities: boolean;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  imports: [CommonModule, MatIconModule, RouterModule, MatButtonModule, MatCardModule, TranslateModule]
})
export class HomeComponent implements OnInit {

  private readonly statsTimeUnit: string = 'months';
  private readonly statsTimeValue: number = 1;

  private readonly manualRefresh$ = new Subject<void>();
  private readonly refresh$ = merge(
    of(void 0),
    this.manualRefresh$,
    this._globals.getMessage().pipe(
      filter(message => message?.text === 'Logged in'),
      map(() => void 0)
    )
  );

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(map(result => result.matches));

  private readonly userName$ = this.refresh$.pipe(
    map(() => localStorage.getItem('Name') ?? 'Scout')
  );

  private readonly orgBrand$ = this.refresh$.pipe(
    map(() => this.getOrganizationBrand())
  );

  private readonly dateLocale$ = this.translate.onLangChange.pipe(
    map((event) => event.lang),
    startWith(this.translate.currentLang || localStorage.getItem('language') || 'en-US'),
    map((lang) => this.normalizeLocale(lang))
  );

  private readonly scoutingStatsState$ = this.refresh$.pipe(
    switchMap(() => this.scoutingFlowService.getScoutingActivityStats(this.statsTimeUnit, this.statsTimeValue).pipe(
      map((stats): LoadingState<ScoutingActivityStatsViewModel | null> => ({ loading: false, data: stats })),
      catchError(() => of({ loading: false, data: null })),
      startWith({ loading: true, data: null })
    )),
    shareReplay(1)
  );

  private readonly scoutInfoState$ = this.refresh$.pipe(
    switchMap(() => this.scoutingFlowService.getScoutingSimpleActivityStats().pipe(
      map((items) => {
        const getValue = (name: string): number | null => {
          const item = (items ?? []).find((x: CountItemModel) => x.Name === name);
          return item?.Value ?? null;
        };

        const scoutInfo: ScoutInfoVm = {
          seasonScoutInfo: {
            reports: getValue('NoOfReportsSeason'),
            notes: getValue('NoOfNotesSeason'),
            gradedGames: getValue('NoOfGamesGradedSeason'),
            scheduleActivities: getValue('NoOfScheduledGamesSeason')
          },
          lifetimeScoutInfo: {
            reports: getValue('NoOfReportsLifetime'),
            notes: getValue('NoOfNotesLifetime'),
            gradedGames: getValue('NoOfGamesGradedLifetime'),
            scheduleActivities: getValue('NoOfScheduledGamesLifetime')
          }
        };

        return { loading: false, data: scoutInfo } as LoadingState<ScoutInfoVm>;
      }),
      catchError(() => of({
        loading: false,
        data: {
          seasonScoutInfo: {
            reports: null,
            notes: null,
            gradedGames: null,
            scheduleActivities: null
          },
          lifetimeScoutInfo: {
            reports: null,
            notes: null,
            gradedGames: null,
            scheduleActivities: null
          }
        }
      })),
      startWith({
        loading: true,
        data: {
          seasonScoutInfo: {
            reports: null,
            notes: null,
            gradedGames: null,
            scheduleActivities: null
          },
          lifetimeScoutInfo: {
            reports: null,
            notes: null,
            gradedGames: null,
            scheduleActivities: null
          }
        }
      })
    )),
    shareReplay(1)
  );

  private readonly reportsState$ = this.refresh$.pipe(
    switchMap(() => this.scoutingFlowService.getAllReports().pipe(
      map((items) => {
        const reports = (items ?? [])
          .filter(item => item.FlowItemType === 'SCREPORT')
          .sort((a, b) => new Date(b.DateCreated).getTime() - new Date(a.DateCreated).getTime())
          .slice(0, 10);

        return { loading: false, data: reports } as LoadingState<FlowItemModel[]>;
      }),
      catchError(() => of({ loading: false, data: [] })),
      startWith({ loading: true, data: [] })
    )),
    shareReplay(1)
  );

  private readonly activitiesState$ = this.refresh$.pipe(
    switchMap(() => this.scheduleService.getScheduleComing().pipe(
      map((data) => {
        const events = (data?.EventList ?? [])
          .sort((a, b) => new Date(a.EventStart).getTime() - new Date(b.EventStart).getTime());

        return {
          loading: false,
          data: {
            comingActivitiesCount: events.length,
            comingActivities: events.slice(0, 4)
          }
        } as LoadingState<{ comingActivitiesCount: number; comingActivities: ScheduleEvent[] }>;
      }),
      catchError(() => of({
        loading: false,
        data: {
          comingActivitiesCount: 0,
          comingActivities: []
        }
      })),
      startWith({
        loading: true,
        data: {
          comingActivitiesCount: 0,
          comingActivities: []
        }
      })
    )),
    shareReplay(1)
  );

  vm$ = combineLatest({
    userName: this.userName$,
    orgBrand: this.orgBrand$,
    dateLocale: this.dateLocale$,
    scoutingStatsState: this.scoutingStatsState$,
    scoutInfoState: this.scoutInfoState$,
    reportsState: this.reportsState$,
    activitiesState: this.activitiesState$
  }).pipe(
    map(({ userName, orgBrand, dateLocale, scoutingStatsState, scoutInfoState, reportsState, activitiesState }): HomeVm => ({
      userName,
      orgName: orgBrand.orgName,
      orgLogoPath: orgBrand.orgLogoPath,
      dateLocale,
      scoutingStats: scoutingStatsState.data,
      isLoadingStats: scoutingStatsState.loading,
      seasonScoutInfo: scoutInfoState.data.seasonScoutInfo,
      lifetimeScoutInfo: scoutInfoState.data.lifetimeScoutInfo,
      isLoadingScoutInfo: scoutInfoState.loading,
      recentReports: reportsState.data,
      isLoadingReports: reportsState.loading,
      comingActivitiesCount: activitiesState.data.comingActivitiesCount,
      comingActivities: activitiesState.data.comingActivities,
      isLoadingActivities: activitiesState.loading
    })),
    shareReplay(1)
  );

  constructor(
    public breakpointObserver: BreakpointObserver,
    public _router: Router,
    public _globals: Globals,
    private scoutingFlowService: ScoutingFlowService,
    private scheduleService: ScheduleService,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this._globals.titleBarText = 'Start';
  }

  private normalizeLocale(locale: string): string {
    const normalized = (locale || 'en-US').replace('_', '-');
    return normalized.length === 2 ? `${normalized.toLowerCase()}-${normalized.toUpperCase()}` : normalized;
  }

  statDisplay(value: number | null): string {
    return value === null ? '-' : `${value}`;
  }

  get welcomeDate(): Date {
    return new Date();
  }

  reportBorderClass(report: FlowItemModel): string {
    if (report.TotalQuality > 3) return 'report-border-green';
    if (report.TotalQuality === 3) return 'report-border-lightgreen';
    if (report.TotalQuality === 2) return 'report-border-yellow';
    if (report.TotalQuality === 1) return 'report-border-red';
    return '';
  }

  openReportDetails(report: FlowItemModel) {
    this.dialog.open(ReportDetailsDialogComponent, {
      width: '100vw',
      maxWidth: '450px',
      height: '100dvh',
      maxHeight: '100dvh',
      panelClass: 'side-dialog',
      position: {
        right: '0'
      },
      data: {
        ReportId: report?.ReportId,
        CanEdit: report?.IsCreatedByCurrentUser
      }
    });
  }

  openComingActivity(activity: ScheduleEvent) {
    const eventType = (activity?.EventType ?? '').trim().toLowerCase();
    const isGameActivity = eventType === 'game';

    if (isGameActivity && activity.GameId) {
      this._router.navigate(['/scouting/gamedetails', activity.GameId]);
      return;
    }

    const dialogRef = this.dialog.open(EventDetailsDialogComponent, {
      maxWidth: '520px',
      width: '92vw',
      panelClass: 'event-details-dialog',
      data: activity,
    });

    dialogRef.afterClosed().subscribe((result: EventDetailsDialogResult | undefined) => {
      if (result?.action === 'delete' && result.eventId) {
        this.scheduleService.removeEventFromSchedule(result.eventId).subscribe(() => {
          this.manualRefresh$.next();
        });
      }
    });
  }

  

  private getOrganizationBrand(): OrganizationBrandVm {
    const orgLogo = localStorage.getItem('OrgLogo')?.trim();
    const orgLogoPath = orgLogo ? `/assets/img/teams/${orgLogo}` : '';

    const preferredOrgName = localStorage.getItem('OrganizationName')
      ?? localStorage.getItem('organizationName')
      ?? localStorage.getItem('OrgName')
      ?? localStorage.getItem('orgName')
      ?? this._globals.TeamName;

    const trimmedName = preferredOrgName?.trim();
    const orgName = trimmedName && trimmedName.toLowerCase() !== 'organization'
      ? trimmedName
      : 'Dalecarlia Scouting';

    return { orgName, orgLogoPath };
  }

  logout() {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userRoles');
    localStorage.removeItem('Name');
    localStorage.removeItem('ToolbarColor');
    localStorage.removeItem('AppColors');
    localStorage.removeItem('isLoggedIn');

    this._router.navigateByUrl('/');
  }

      getIconPath(icon: string): string {
  
      icon = icon.toLowerCase();
      if (icon.startsWith('/')) {
        return `../assets/img${icon}`;
      } else {
        return `../assets/img/${icon}`;
      }
    }
    
  
    handleImageError(event: Event): void {
      const imgElement = event.target as HTMLImageElement;
      imgElement.src = '../assets/img/placeholder.png';
      imgElement.style.width = '30px';
      imgElement.style.height = '30px';
      imgElement.style.objectFit = 'contain';
    }
}
